
import React, { useState } from "react";
import peerRecognitionHero from "@/assets/peer-recognition-hero.webp";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Award, Slack, Wand, Heart, Zap, Star, Tag, Play, ArrowRight } from "lucide-react";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TrustLogosSection from "@/components/trust/TrustLogosSection";
import { usePostHog } from "@/hooks/usePostHog";

const PeerRecognition = () => {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [demoEmail, setDemoEmail] = useState<string | null>(null);
  const { posthog } = usePostHog();

  return (
    <>
      <Helmet>
        <title>Peer-to-Peer Recognition Software | Employee Recognition Platform - Assembly</title>
        <meta name="description" content="Build real culture with Assembly's peer-to-peer recognition platform. Celebrate teammates easily, personalize recognition with points and values, and drive engagement from Slack or Teams." />
        <meta name="keywords" content="peer recognition software, employee recognition platform, core values reinforcement, recognition AI assistant, Slack integration, Teams integration" />
        <meta property="og:title" content="Peer-to-Peer Recognition That Builds Real Culture" />
        <meta property="og:description" content="Celebrate teammates easily, personalize recognition with points and values, and drive real engagement — all from Slack, Teams, or the web." />
        <meta property="og:site_name" content="Assembly" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Peer-to-Peer Recognition That Builds Real Culture" />
        <meta name="twitter:description" content="Celebrate teammates easily, personalize recognition with points and values, and drive real engagement — all from Slack, Teams, or the web." />
        <meta name="twitter:image" content="https://lovable.dev/opengraph-image-p98pqg.png" />
        {typeof window !== 'undefined' && <link rel="canonical" href={window.location.href} />}
        
        {/* Schema markup for Software Application */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "name": "Assembly Peer-to-Peer Recognition Platform",
              "applicationCategory": "BusinessApplication",
              "operatingSystem": "Web",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              },
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": "4.9",
                "ratingCount": "1000"
              },
              "description": "Build real culture with Assembly's peer-to-peer recognition platform. Celebrate teammates easily, personalize recognition with points and values, and drive engagement from Slack or Teams."
            }
          `}
        </script>
      </Helmet>
      
      <div className="flex flex-col min-h-screen w-full bg-white">
        <Navbar />
        
        <main className="flex-grow w-full">
          {/* Full Height Hero Section - Matching Main Landing Page */}
          <div className="relative bg-gradient-to-b from-slate-50 via-blue-50 to-indigo-100 font-sans overflow-hidden min-h-screen">
            {/* Modern Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-blue-200/30 to-purple-200/30 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute top-1/2 -left-32 w-80 h-80 bg-gradient-to-br from-emerald-200/25 to-blue-200/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
              <div className="absolute bottom-0 right-1/3 w-64 h-64 bg-gradient-to-br from-orange-200/25 to-pink-200/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>
            
            {/* Grid Pattern Overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
                backgroundSize: '50px 50px'
              }}></div>
            </div>

            <section className="relative flex items-center justify-center py-8 lg:py-10 px-4 lg:px-8 min-h-screen">
              <div className="container mx-auto px-4 2xl:px-12 max-w-none lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] relative px-6 lg:px-6 xl:px-8 pb-3 lg:pb-4 pt-20 lg:pt-24">
                <div className="flex flex-col lg:grid lg:grid-cols-7 gap-6 lg:gap-4 items-start">
                  {/* Left Section: Text Content */}
                  <div className="flex flex-col items-start text-left lg:col-span-4 h-full justify-center space-y-4 lg:space-y-6">
                    {/* Pills */}
                    <motion.div 
                      className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3 mt-6 lg:mt-8 mb-3 lg:mb-4"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.7 }}
                    >
                      <div className="relative overflow-hidden px-3 py-1.5 rounded-full bg-black/5 border border-black/10 backdrop-blur-sm hover:bg-black/10 transition-all duration-300 hover:-translate-y-0.5">
                        <h1 className="text-xs font-normal text-gray-900 tracking-wide uppercase">Best Employee Peer-to-Peer Recognition Software</h1>
                      </div>
                    </motion.div>

                    {/* Main Heading */}
                    <div className="mb-4 lg:mb-6">
                      <motion.h2 
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-5xl xl:text-6xl font-bold text-black tracking-tight font-sans"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        Peer-to-Peer Recognition that Builds Real Culture
                      </motion.h2>
                    </div>

                    {/* Subheading */}
                    <motion.p 
                      className="text-sm sm:text-base lg:text-base xl:text-lg text-gray-600 mb-6 lg:mb-8 leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      Celebrate wins, big or small — directly in Slack, Teams, or web. With Assembly, peer-to-peer recognition feels natural, strengthens culture, and delivers measurable business impact.
                    </motion.p>
                    
                    {/* Action Buttons */}
                    <motion.div 
                      className="mb-4 lg:mb-6 w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.6 }}
                    >
                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
                        <Button 
                          size="lg" 
                          className="group relative w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 lg:py-4 lg:px-8 xl:py-5 xl:px-10 2xl:py-6 2xl:px-12 rounded-xl text-sm sm:text-base lg:text-base xl:text-lg shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/40 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] border-0 overflow-hidden" 
                          onClick={() => setIsDemoModalOpen(true)}
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                          <span className="relative z-10 flex items-center justify-center gap-2">
                            Book a Demo
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </span>
                        </Button>
                        <Button 
                          variant="outline" 
                          size="lg" 
                          className="group relative w-full sm:w-auto border-2 border-blue-200 bg-white/80 backdrop-blur-sm text-blue-700 hover:text-blue-800 hover:bg-blue-50/80 hover:border-blue-300 font-semibold py-3 px-6 lg:py-4 lg:px-8 xl:py-5 xl:px-10 2xl:py-6 2xl:px-12 rounded-xl text-sm sm:text-base lg:text-base xl:text-lg shadow-md shadow-blue-100/50 hover:shadow-lg hover:shadow-blue-200/60 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] overflow-hidden" 
                          onClick={() => {
                            posthog?.capture('see_it_in_action_clicked', {
                              page: '/employee-recognition/peer-recognition',
                              section: 'hero',
                              button_text: 'See it in Action',
                              url: window.location.href,
                              opens_modal: true
                            });
                            setIsSupademoModalOpen(true);
                          }}
                        >
                          <span className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                          <span className="relative z-10 flex items-center justify-center gap-2">
                            See it in Action
                            <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" fill="currentColor" />
                          </span>
                        </Button>
                      </div>
                    </motion.div>
                    
                    {/* Trust Ratings */}
                    <motion.div 
                      className="flex flex-col sm:flex-row gap-3 sm:gap-6 items-start sm:items-center w-full"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.8 }}
                    >
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400 fill-yellow-400" />
                        <span className="text-sm font-semibold text-gray-700">4.9 / 5 on G2</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-500">3000+ reviews</span>
                    </motion.div>
                  </div>
                  
                  {/* Right Section: Hero Image */}
                  <div className="relative w-full h-full lg:col-span-3 mt-6 lg:mt-0">
                    <motion.img 
                      src={peerRecognitionHero} 
                      alt="Peer recognition interface showing Lee W. giving recognition to Lucy Wang with engagement reactions"
                      className="w-full h-full object-contain"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    />
                  </div>
                </div>
                
                {/* Trust Section at Bottom */}
                <motion.div 
                  className="text-left pt-4 lg:pt-6 mt-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.0 }}
                >
                  <p className="text-lg text-gray-600 mb-6 text-center">
                    Trusted by top teams to power meaningful recognition and employee rewards.
                  </p>
                  <TrustLogosSection />
                </motion.div>
              </div>
            </section>
          </div>

          {/* Why Teams Care Section */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <motion.div 
                className="text-center mb-12 lg:mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6">
                  <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                    Core Benefits
                  </span>
                </div>
                <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  Why Teams <span className="text-blue-600">Care</span>
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Recognition that works starts with making it effortless, authentic, and impactful for everyone.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                {/* Block 1: Frictionless Recognition */}
                <motion.div 
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 bg-gradient-to-br from-slate-50 to-slate-100/50">
                    <CardContent className="p-8">
                      <div className="flex items-center mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl mr-4">
                          <Zap className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-200">
                            Effortless
                          </Badge>
                          <h3 className="text-xl font-bold text-gray-900">
                            Frictionless Recognition Anywhere
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        Recognition only works when it's effortless. With Assembly, employees can give recognition in Slack, Teams, or web — in seconds, without breaking their flow.
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Slack className="h-4 w-4" />
                        <span>Slack & Teams Integration</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Block 2: Values-Driven */}
                <motion.div 
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 bg-gradient-to-br from-blue-50 to-blue-100/50">
                    <CardContent className="p-8">
                      <div className="flex items-center mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl mr-4">
                          <Tag className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-2 bg-blue-100 text-blue-700 hover:bg-blue-200">
                            Authentic
                          </Badge>
                          <h3 className="text-xl font-bold text-gray-900">
                            Values-Driven & Authentic
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        Tie recognition to company values so every "thank you" reinforces what matters most. From innovation to teamwork, employees celebrate each other in ways that reflect your culture.
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Award className="h-4 w-4" />
                        <span>Custom Value Tags</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Block 3: Visible & Belonging */}
                <motion.div 
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 bg-gradient-to-br from-purple-50 to-purple-100/50">
                    <CardContent className="p-8">
                      <div className="flex items-center mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl mr-4">
                          <Heart className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-2 bg-purple-100 text-purple-700 hover:bg-purple-200">
                            Belonging
                          </Badge>
                          <h3 className="text-xl font-bold text-gray-900">
                            Visible & Belonging-Centric
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        Recognition is more powerful when it's shared. Assembly creates a recognition feed where peers, managers, and leaders can react, comment, and celebrate together — building real belonging.
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Users className="h-4 w-4" />
                        <span>Social Recognition Feed</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Block 4: Measurable Impact */}
                <motion.div 
                  className="group relative"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 bg-gradient-to-br from-orange-50 to-orange-100/50">
                    <CardContent className="p-8">
                      <div className="flex items-center mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl mr-4">
                          <Wand className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-2 bg-orange-100 text-orange-700 hover:bg-orange-200">
                            Impact
                          </Badge>
                          <h3 className="text-xl font-bold text-gray-900">
                            Measurable Business Impact
                          </h3>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed mb-6">
                        Go beyond "feel-good." Assembly tracks participation, adoption, and engagement, giving HR and leadership the insights they need to prove ROI.
                      </p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Star className="h-4 w-4" />
                        <span>Analytics Dashboard</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Core Values Feature */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-gradient-to-b from-white to-gray-50/30">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div 
                  className="order-2 lg:order-1"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="relative">
                    <img
                      src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6752a4f154fb8217d708fdf7_core%20values.avif"
                      alt="Custom Core Values Interface showing company values integration and recognition tagging"
                      className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
                    />
                    <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                      Values-Based Recognition
                    </div>
                  </div>
                </motion.div>
                <motion.div 
                  className="order-1 lg:order-2"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="inline-flex items-center px-4 py-2 bg-orange-100 rounded-full mb-6">
                    <span className="text-xs font-bold text-orange-700 uppercase tracking-wider">
                      Core Values Integration
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Reinforce What <span className="text-orange-600">Matters Most</span>
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Recognize and reinforce the values unique to your company. Transform abstract values into daily actions through meaningful peer recognition.
                  </p>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Gamify appreciation with custom branding, value tags, recognition rules, and point logic that reflects your company's identity.
                  </p>
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                    <Award className="h-6 w-6 text-orange-600 mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-lg font-semibold text-gray-900 block mb-1">Customizable Core Values & Point System</span>
                      <span className="text-sm text-gray-600">Align recognition with your company's unique culture and values</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>


          {/* Dora AI Recognition Writing */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div 
                  className="order-1 lg:order-1"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center px-4 py-2 bg-purple-100 rounded-full mb-6">
                    <span className="text-xs font-bold text-purple-700 uppercase tracking-wider">
                      AI-Powered Writing
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Recognition Writing, <span className="text-purple-600">Made Easy</span>
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    DoraAI helps your team craft thoughtful, on-brand recognition messages in seconds — making appreciation effortless and meaningful.
                  </p>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    No more writer's block or generic messages. Get personalized suggestions that capture genuine appreciation and align with your company values.
                  </p>
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                    <Wand className="h-6 w-6 text-purple-600 mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-lg font-semibold text-gray-900 block mb-1">AI Recognition Writing Assistant</span>
                      <span className="text-sm text-gray-600">Craft meaningful messages with AI-powered suggestions</span>
                    </div>
                  </div>
                </motion.div>
                <motion.div 
                  className="order-2 lg:order-2"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="relative">
                    <img
                      src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6752a856de323c3f64ba7f12_aiiii.avif"
                      alt="DoraAI Recognition Writing Assistant interface showing AI-powered message suggestions"
                      className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
                    />
                    <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                      AI-Powered Messages
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Slack & Teams Integration */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-gradient-to-b from-gray-50/30 to-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div 
                  className="order-2 lg:order-1"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6">
                    <span className="text-xs font-bold text-blue-700 uppercase tracking-wider">
                      Platform Integration
                    </span>
                  </div>
                  <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                    Integrated Where <span className="text-blue-600">You Work</span>
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    Give and receive recognition in Slack or Microsoft Teams, or directly inside Assembly. No need to switch between platforms.
                  </p>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Assembly meets employees where they are — boosting usage and reducing friction for maximum participation.
                  </p>
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <Slack className="h-6 w-6 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-lg font-semibold text-gray-900 block mb-1">Seamless Teams and Slack Integrations</span>
                      <span className="text-sm text-gray-600">Recognition flows naturally within your existing workflows</span>
                    </div>
                  </div>
                </motion.div>
                <motion.div 
                  className="order-1 lg:order-2"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="relative">
                    <img
                      src="https://cdn.prod.website-files.com/6058ba87eec5717595807524/6752a75e8f757cda3e5b17d2_slack.avif"
                      alt="Slack and Teams Integration showing peer recognition notifications and workflows"
                      className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
                    />
                    <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                      Native Integration
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Modern Stats Section */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-gradient-to-br from-orange-50 via-white to-blue-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
              <motion.div 
                className="text-center mb-16"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-blue-100 rounded-full mb-6">
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Proven Results
                  </span>
                </div>
                <h2 className="text-3xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                  The Business Impact of <span className="text-orange-600">Peer Recognition</span>
                </h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  Companies using Assembly's peer recognition platform see real, measurable results across engagement, retention, and culture metrics:
                </p>
              </motion.div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {[
                  { stat: "92%", label: "of employees feel more appreciated", color: "orange", delay: 0.1 },
                  { stat: "67%", label: "higher employee engagement", color: "blue", delay: 0.2 },
                  { stat: "41%", label: "decrease in voluntary turnover", color: "green", delay: 0.3 },
                  { stat: "4.8/5", label: "average user satisfaction", color: "purple", delay: 0.4 }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: item.delay }}
                    whileHover={{ scale: 1.05 }}
                    className="group"
                  >
                    <Card className={`text-center border-0 shadow-lg hover:shadow-2xl transition-all duration-300 bg-gradient-to-br ${
                      item.color === 'orange' ? 'from-orange-50 to-orange-100 hover:from-orange-100 hover:to-orange-200' :
                      item.color === 'blue' ? 'from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200' :
                      item.color === 'green' ? 'from-green-50 to-green-100 hover:from-green-100 hover:to-green-200' :
                      'from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200'
                    } rounded-2xl overflow-hidden`}>
                      <CardContent className="pt-8 pb-8">
                        <div className={`text-4xl lg:text-5xl font-bold mb-4 ${
                          item.color === 'orange' ? 'text-orange-600' :
                          item.color === 'blue' ? 'text-blue-600' :
                          item.color === 'green' ? 'text-green-600' :
                          'text-purple-600'
                        } group-hover:scale-110 transition-transform duration-300`}>
                          {item.stat}
                        </div>
                        <p className="text-gray-700 font-medium leading-relaxed">{item.label}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 sm:py-20 2xl:py-24 bg-gradient-to-r from-orange-600 to-blue-600">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Ready to Build a Culture of <span className="text-orange-200">Appreciation</span>?
                </h2>
                <p className="text-lg sm:text-xl text-orange-100 mb-8 leading-relaxed">
                  Join 4,000+ companies who've made recognition a daily habit with Assembly.
                </p>
                <Button 
                  size="lg" 
                  className="bg-white text-orange-600 hover:bg-orange-50 font-semibold py-4 px-8 rounded-lg text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => setIsDemoModalOpen(true)}
                >
                  Book My Demo
                </Button>
              </motion.div>
            </div>
          </section>

        </main>
        
        <Footer />
      </div>

      {/* Create a hidden section for the demo to scroll to */}
      <div id="demo" className="scroll-mt-24"></div>
      
      <DemoBookingModal 
        open={isDemoModalOpen} 
        onOpenChange={setIsDemoModalOpen} 
      />
      <SupademoModal 
        open={isSupademoModalOpen} 
        onOpenChange={setIsSupademoModalOpen} 
        email={demoEmail} 
      />
    </>
  );
};

export default PeerRecognition;
